import { motion } from 'framer-motion';
import { Users, FileCheck, TrendingUp, BookOpen, AlertTriangle, BarChart3 } from 'lucide-react';
import { useSchoolAnalytics } from '@/hooks/useSchoolAdmin';

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 200, damping: 22 } },
};

const INTEREST_LABELS: Record<string, string> = {
  'ai-tech': 'AI & Technology', arts: 'Art & Design', business: 'Business', science: 'Science',
  'helping-people': 'Helping People', health: 'Health', media: 'Media & Content', teaching: 'Teaching',
  sports: 'Sports', music: 'Music', environment: 'Environment', gaming: 'Gaming', fashion: 'Fashion',
  cooking: 'Food & Cooking', writing: 'Writing', psychology: 'Psychology', animals: 'Animals',
  law: 'Law & Debate', building: 'Engineering', money: 'Finance', 'social-impact': 'Social Impact',
  education: 'Education', finance: 'Finance', data: 'Data & Analytics',
};

interface AdminDashboardProps {
  schoolId: string;
  courseCount?: number;
}

const AdminDashboard = ({ schoolId }: AdminDashboardProps) => {
  const { data, loading, error } = useSchoolAnalytics(schoolId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="glass-card p-8 text-center text-muted-foreground">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-accent" />
        <p>Could not load analytics. {error}</p>
      </div>
    );
  }

  const completionRate = data.total_students > 0
    ? Math.round((data.total_assessments / data.total_students) * 100)
    : 0;

  const maxInterest = Math.max(...data.interest_distribution.map(i => i.student_count), 1);

  return (
    <motion.div className="space-y-8" variants={stagger} initial="hidden" animate="show">
      {/* Summary Cards */}
      <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={fadeUp}>
        <SummaryCard icon={Users} label="Total Students" value={data.total_students} />
        <SummaryCard icon={FileCheck} label="Assessments" value={data.total_assessments} />
        <SummaryCard icon={TrendingUp} label="This Month" value={data.completed_this_month} />
        <SummaryCard icon={BarChart3} label="Completion Rate" value={`${completionRate}%`} accent={completionRate < 50} />
      </motion.div>

      {/* Interest Distribution */}
      <motion.div variants={fadeUp} className="glass-card p-6 space-y-4">
        <h3 className="font-display font-bold text-foreground flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" />
          What Students Are Interested In
        </h3>
        {data.interest_distribution.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assessment data yet.</p>
        ) : (
          <div className="space-y-3">
            {data.interest_distribution.slice(0, 10).map((item, i) => (
              <div key={item.interest} className="flex items-center gap-3">
                <span className="text-sm text-foreground w-36 truncate">
                  {INTEREST_LABELS[item.interest] || item.interest}
                </span>
                <div className="flex-1 h-6 bg-secondary rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-primary/80"
                    initial={{ width: 0 }}
                    animate={{ width: `${(item.student_count / maxInterest) * 100}%` }}
                    transition={{ delay: 0.2 + i * 0.05, duration: 0.5 }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-8 text-right">{item.student_count}</span>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Path Distribution + Alignment Gaps side by side */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Path Distribution */}
        <motion.div variants={fadeUp} className="glass-card p-6 space-y-4">
          <h3 className="font-display font-bold text-foreground">Career Path Choices</h3>
          {data.path_distribution.length === 0 ? (
            <p className="text-sm text-muted-foreground">No paths generated yet.</p>
          ) : (
            <div className="space-y-3">
              {data.path_distribution.map((path) => (
                <div key={path.path_name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      path.risk_level === 'conservative' ? 'bg-accent' :
                      path.risk_level === 'moderate' ? 'bg-primary' : 'bg-foreground'
                    }`} />
                    <span className="text-sm text-foreground">{path.path_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{path.chosen_count} students</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Alignment Gaps */}
        <motion.div variants={fadeUp} className="glass-card p-6 space-y-4">
          <h3 className="font-display font-bold text-foreground flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-accent" />
            Alignment Gaps
          </h3>
          <p className="text-xs text-muted-foreground">
            Where student interests don't match your course/club offerings.
          </p>
          {data.interest_distribution.length === 0 ? (
            <p className="text-sm text-muted-foreground">Need assessment data to identify gaps.</p>
          ) : (
            <div className="space-y-2">
              {data.interest_distribution.slice(0, 5).map((item) => (
                <div key={item.interest} className="flex items-center justify-between p-2 rounded-lg bg-accent/5 border border-accent/10">
                  <span className="text-sm text-foreground">
                    {INTEREST_LABELS[item.interest] || item.interest}
                  </span>
                  <span className="text-xs font-medium text-accent">
                    {item.student_count} students interested
                  </span>
                </div>
              ))}
              <p className="text-xs text-muted-foreground italic mt-2">
                Review your courses and clubs to ensure alignment with student interests.
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Monthly Trend */}
      {data.monthly_trend.length > 0 && (
        <motion.div variants={fadeUp} className="glass-card p-6 space-y-4">
          <h3 className="font-display font-bold text-foreground flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Assessment Trend (Last 12 Months)
          </h3>
          <div className="flex items-end gap-2 h-32">
            {data.monthly_trend.map((month, i) => {
              const maxVal = Math.max(...data.monthly_trend.map(m => m.assessments), 1);
              const height = (month.assessments / maxVal) * 100;
              return (
                <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                  <motion.div
                    className="w-full bg-primary/70 rounded-t-md"
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: 0.3 + i * 0.05, duration: 0.4 }}
                  />
                  <span className="text-[9px] text-muted-foreground">{month.month.split('-')[1]}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Quick Stats Footer */}
      <motion.div variants={fadeUp} className="flex items-center gap-6 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <BookOpen className="w-3 h-3" /> {data.course_count} courses
        </span>
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" /> {data.club_count} clubs
        </span>
      </motion.div>
    </motion.div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, accent }: { icon: React.ElementType; label: string; value: string | number; accent?: boolean }) => (
  <motion.div variants={fadeUp} className="glass-card p-5 space-y-2">
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${accent ? 'text-accent' : 'text-primary'}`} />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
    <p className={`text-2xl font-display font-bold ${accent ? 'text-accent' : 'text-foreground'}`}>{value}</p>
  </motion.div>
);

export default AdminDashboard;
